////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.1                                                            //
//  Summary: GET-request hook — loading/data/error over the shared axios instance //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { useEffect, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';

import api from '@/lib/api';

export type ApiState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'success'; data: T; error: null }
  | { status: 'error'; data: null; error: Error };

/** Fetches `url` on mount and whenever it changes. Genuine synchronisation with an
 * external system (the network) — the abort on cleanup is the point: an in-flight
 * request from an unmounted component (or a superseded `url`) must not resolve into
 * state that no longer exists. No feature in 1.0.1 calls this yet (there is no
 * public GET route to call before 1.0.2), but it is exercised by nothing else in
 * this phase and is provided as the house pattern for 1.0.2 to build on. */
export function useApi<T> (url: string | null, config?: AxiosRequestConfig): ApiState<T> {
  const [state, setState] = useState<ApiState<T>>({ status: 'loading', data: null, error: null });

  useEffect(() => {
    if (!url) return;

    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting to the loading state before issuing a new request IS the synchronisation this effect performs (a fresh `url` is new external-system state to reflect); the actual subscription is the .then/.catch pair below.
    setState({ status: 'loading', data: null, error: null });

    api
      .get<T>(url, { ...config, signal: controller.signal })
      .then((response) => setState({ status: 'success', data: response.data, error: null }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({ status: 'error', data: null, error: error instanceof Error ? error : new Error('Request failed') });
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `config` is caller-supplied and typically a fresh object literal per render; re-running the request on url change only is the intended contract.
  }, [url]);

  return state;
}
